import { BasicModal, Loading, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import { useBoolean } from "@/hooks/useBoolean";
import useProfile from "@/hooks/useProfile";
import { QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError, pluralize } from "@/libraries/helper";
import {
	getCasePatient,
	getCaseQueryOptions,
	getEncounterQueryOptions,
} from "@/queries";
import { CaseExistPatientSearchSchema } from "@/schemas";
import type { Case } from "@/types";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import {
	onlineManager,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute(
	"/dashboard/case/create/exist/patient/$patientIc",
)({
	params: {
		parse: (params) => ({
			patientIc: z.coerce.string().parse(params.patientIc),
		}),
		stringify: ({ patientIc }) => ({ patientIc }),
	},
	loader: ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureQueryData(
				getCasePatient(params.patientIc),
			);
		}
		return context.queryClient.getQueryData([
			QUERY_KEYS.GET_CASE_PATIENT,
			params.patientIc,
		]);
	},
	validateSearch: zodSearchValidator(CaseExistPatientSearchSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const patientIc = Route.useParams({
		select: (state) => state.patientIc,
	});
	const encounterId = Route.useSearch({
		select: (state) => state.encounter_id,
	});
	const reference = Route.useSearch({
		select: (state) => state.reference,
	});
	const onBackward = useBackward();
	const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
	const { profile } = useProfile();

	const {
		value: isOpenPendingInvitation,
		// setTrue: openPendingInvitation,
		setFalse: closePendingInvitation,
	} = useBoolean();

	const encounterQuery = useQuery(
		getEncounterQueryOptions(encounterId as number),
	);
	const caseQuery = useQuery(
		getCaseQueryOptions(selectedCaseId as number, !!selectedCaseId, {
			retry: 0,
		}),
	);

	const casePatientQuery = useSuspenseQuery(getCasePatient(patientIc));
	const patient = casePatientQuery.data.patient;

	const caseCount = useMemo(
		() => encounterQuery.data?.cases?.length ?? patient?.cases?.length ?? 0,
		[encounterQuery, patient],
	);

	const cases = useMemo(() => {
		if (encounterId && encounterQuery.data) {
			return encounterQuery.data.cases ?? [];
		}
		return patient.cases ?? [];
	}, [encounterId, encounterQuery.data, patient.cases]);

	const selectedCase = useMemo(
		() => cases.find((c) => c.id === selectedCaseId),
		[cases, selectedCaseId],
	);

	const handleOnClickCase = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, caseValue: Case) => {
			e.preventDefault();
			// const pendindMmeberIds = new Set([
			// 	...(caseValue.pending_invitations.map((p) => p.id) ?? []),
			// 	...(caseValue.external_pending_invitations.map((p) => p.id) ?? []),
			// ]);
			// const isHavePendingInvitation = pendindMmeberIds.has(
			// 	profile?.id as number,
			// );
			// if (isHavePendingInvitation) {
			// 	return openPendingInvitation();
			// }
			return setSelectedCaseId(caseValue.id);
		},
		[],
	);

	const onClickAddNewCase = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (!encounterId) {
			return navigate({
				to: "/dashboard/case/create",
				search: {
					step: 2,
					...(patient.id ? { pid: patient.id } : {}),
					...(patient.dob ? { pdob: patient.dob } : {}),
					...(patient.sex ? { psex: patient.sex } : {}),
					...(patient.name ? { pname: patient.name } : {}),
					...(patient.surname ? { psurname: patient.surname } : {}),
					...(patient.address ? { paddress: patient.address } : {}),
					...(patient.ic_number ? { pic: patient.address } : {}),
				},
			});
		}
		return navigate({
			to: "/dashboard/case/create",
			search: {
				step: 2,
				encounter_id: encounterId,
			},
		});
	};

	const handleOnRetieveCase = useCallback(
		(caseItem: NonNullable<(typeof caseQuery)["data"]>) => {
			if (!profile) return;
			const caseExistMemberIds = Array.from(
				new Set([
					...caseItem.members.map((m) => m.id),
					...caseItem.internal_members.map((m) => m.id),
				]),
			);
			// const casePendingMemberIds = Array.from(
			// 	new Set([...caseItem.pending_members.map((m) => m.id)]),
			// );
			const currentUserStatus =
				caseItem.user.id === profile.id
					? ("owner" as const)
					: caseExistMemberIds.includes(profile.id)
						? ("member" as const)
						: // : casePendingMemberIds.includes(profile.id)
							// 	? ("pending" as const)
							("noaccess" as const);
			if (currentUserStatus === "owner" || currentUserStatus === "member") {
				return navigate({
					to: "/dashboard/case/view/$id",
					params: {
						id: selectedCaseId,
					},
				});
			}
			if (currentUserStatus === "noaccess") {
				navigate({
					to: "/dashboard/case/create/exist/request/$caseId",
					params: {
						caseId: selectedCaseId,
					},
					search: {
						case_name: selectedCase?.title,
					},
				});
			}
			// if (currentUserStatus === "pending") {
			// 	return openPendingInvitation();
			// }
		},
		[profile, navigate, selectedCaseId, selectedCase?.title],
	);

	const handleOnErrorCase = useCallback(() => {
		const resData = caseQuery.error?.response?.data as Record<
			"messages",
			string
		>;
		const isNoAccess = resData?.messages
			?.toLowerCase()
			?.includes("you don't have privilege to access");
		if (!isNoAccess) return;
		return navigate({
			to: "/dashboard/case/create/exist/request/$caseId",
			params: {
				caseId: selectedCaseId,
			},
			search: {
				case_name: selectedCase?.title,
				pid: patient.id,
				pdob: patient.dob,
				psex: patient.sex,
			},
		});
	}, [caseQuery.error, patient, navigate, selectedCaseId, selectedCase?.title]);

	const onClosePendingModal = useCallback(() => {
		setSelectedCaseId(null);
		return closePendingInvitation();
	}, [closePendingInvitation]);

	const onAcceptInvitation = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			navigate({
				to: "/dashboard/inbox/alerts",
				search: {
					id: selectedCaseId,
					type: "Case",
					action: "accept",
				},
			});
			return onClosePendingModal();
		},
		[onClosePendingModal, navigate, selectedCaseId],
	);
	const onDeclineInvitation = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			navigate({
				to: "/dashboard/inbox/alerts",
				search: {
					id: selectedCaseId,
					type: "Case",
					action: "reject",
				},
			});
			return onClosePendingModal();
		},
		[onClosePendingModal, navigate, selectedCaseId],
	);

	const onBack = useCallback(() => {
		return onBackward(reference);
	}, [onBackward, reference]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (caseQuery.data && caseQuery.data.id === selectedCaseId) {
			handleOnRetieveCase(caseQuery.data);
		}
	}, [selectedCaseId, caseQuery]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (caseQuery.isError && isAxiosError(caseQuery.error, 404)) {
			handleOnErrorCase();
		}
	}, [caseQuery.isError, caseQuery.error]);

	return (
		<>
			<BackAppBar onBack={onBack}>
				<div className="container pb-2">
					<Typography fontWeight={600} fontSize={28}>
						{pluralize({
							count: caseCount,
							singularText: "case",
						})}{" "}
						found for this patient
					</Typography>
				</div>
			</BackAppBar>
			<div className="container h-full overflow-y-auto">
				<div className="flex flex-col gap-4 py-4 justify-center items-center">
					<Card variant="outlined" className="w-full !rounded-xl">
						<CardContent
							classes={{
								root: "!pb-4",
							}}
						>
							<div className="flex flex-col gap-4">
								<Typography fontWeight={700} fontSize={14}>
									Patient's Details
								</Typography>
								<div className="flex flex-col gap-1">
									<Typography fontWeight={400} fontSize={14}>
										Patient's ID
									</Typography>
									<Typography fontWeight={500} fontSize={14}>
										{patient.ic_number}
									</Typography>
									<Typography fontWeight={500} fontSize={14}>
										{[patient?.name, patient?.surname].join(" ")}
									</Typography>
								</div>
								<div className="grid grid-cols-2">
									<div className="flex flex-col gap-1">
										<Typography fontWeight={400} fontSize={14}>
											Date Of Birth
										</Typography>
										<Typography fontWeight={500} fontSize={14}>
											{patient?.dob
												? dayjs(patient.dob).format("DD MMMM YYYY")
												: "-"}
										</Typography>
									</div>
									<div className="flex flex-col gap-1">
										<Typography fontWeight={400} fontSize={14}>
											Gender
										</Typography>
										<Typography fontWeight={500} fontSize={14}>
											{patient?.sex ?? "-"}
										</Typography>
									</div>
								</div>
								<Divider />
								<div className="flex flex-col gap-1">
									<Typography fontWeight={400} fontSize={14}>
										Address
									</Typography>
									<Typography fontWeight={500} fontSize={14}>
										{patient?.address ?? "-"}
									</Typography>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card variant="outlined" className="w-full !rounded-xl">
						<CardContent
							classes={{
								root: "!pb-4",
							}}
						>
							<div className="flex flex-col gap-4">
								<div className="flex flex-row justify-between items-center">
									<Typography fontWeight={700} fontSize={14}>
										Cases
									</Typography>
									<Button
										variant="contained"
										color="primary"
										size="small"
										className="!rounded-full"
										startIcon={<AddOutlinedIcon />}
										onClick={onClickAddNewCase}
									>
										Add Case
									</Button>
								</div>
								{cases?.length ? (
									<List className="w-full">
										{cases.map((c) => (
											<Fragment key={c.id}>
												<ListItem
													disablePadding
													secondaryAction={<ChevronRightOutlinedIcon />}
												>
													<ListItemButton
														dense
														onClick={(e) => handleOnClickCase(e, c)}
													>
														<ListItemText
															primary={c.title ?? "-"}
															secondary={
																<>
																	<Typography
																		component="span"
																		fontWeight={500}
																		fontSize={14}
																		className="block"
																	>
																		{isEmpty(c.summary) ? "-" : c.summary}
																	</Typography>
																	<Typography
																		component="span"
																		fontWeight={400}
																		fontSize={12}
																		className="block"
																	>
																		{[
																			c.status === "active"
																				? "Active"
																				: "Archived",
																			`Last updated ${dayjs(
																				c.conversation_updated_at,
																			).format("DD MMM YYYY, hh:mm A")}`,
																		].join(" • ")}
																	</Typography>
																</>
															}
														/>
													</ListItemButton>
												</ListItem>
												<Divider variant="fullWidth" component="li" />
											</Fragment>
										))}
									</List>
								) : null}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
			<BasicModal
				isOpen={isOpenPendingInvitation}
				onClose={onClosePendingModal}
			>
				<div className="flex flex-col gap-4">
					<Typography fontWeight={600} fontSize={16} align="center">
						Pending Invitation
					</Typography>
					<Typography fontWeight={400} fontSize={14} align="center">
						You have a pending invitation to join a case team that requires your
						response. Please choose to accept or decline the invitation.
					</Typography>
					<Button
						fullWidth
						variant="contained"
						color="primary"
						size="large"
						className="!rounded-full"
						onClick={onAcceptInvitation}
					>
						Accept
					</Button>
					<Button
						fullWidth
						variant="contained"
						color="error"
						size="large"
						className="!rounded-full"
						onClick={onDeclineInvitation}
					>
						Decline
					</Button>
					<Button
						fullWidth
						variant="outlined"
						color="primary"
						size="large"
						className="!rounded-full"
						onClick={onClosePendingModal}
					>
						Cancel
					</Button>
				</div>
			</BasicModal>
			<Loading status={caseQuery.isLoading || encounterQuery.isLoading} />
		</>
	);
}
